import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useEnvironmentShowing } from '~/hooks/environment/useEnvironmentShowing';
import { useEnvironmentViews } from '~/hooks/environment/useEnvironmentViews';
import { Orientations } from '~/engine/view/views/PlaneView';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { seriesMipPreviewVisibleState } from '~/state/atoms/viewerAtoms';
import { seriesNameOf } from '~/utils/transforms/imageDataTransforms';
import { GridWidget } from '~/viewportGrid';
import { focusGridPoint } from '~/viewportGrid/adapters/engine';
import FittedSeriesMipPane from './FittedSeriesMipPane';
import { seriesInstanceUidOf } from '~/domain/dicom/uids';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import { warnWhenLocationNotShown } from './warnWhenLocationNotShown';

/** CSS px. */
const OPENING_SIZE = { widthPx: 220, heightPx: 300 };

/**
 * The primary image's overview widget, while the toggle is on. Its slice line
 * marks the row's transversal tile plane, and it offers a click only while a
 * tile shows the image.
 */
export function useImageOverviewWidget(): ReadonlyArray<GridWidget> {
    const intl = useIntl();
    const isOpen = useRecoilValue(seriesMipPreviewVisibleState);
    const setOpen = useSetRecoilState(seriesMipPreviewVisibleState);
    const close = useCallback(() => setOpen(false), [setOpen]);
    const image = useSelectedPrimaryImage();
    // A rename changes the series name in place.
    useDomainVersion();
    const seriesDescription = image?.seriesMetadata.seriesDescription;
    const environment = useEnvironmentShowing(image);
    const views = useEnvironmentViews(environment);
    const viewManager = useRecoilValue(viewManagerState);
    // The row's own image, so its grid is the frame the tiles draw in.
    const locate = useCallback(
        (voxelIndex: number) => {
            if (!image) {
                return;
            }
            warnWhenLocationNotShown(
                focusGridPoint(
                    getDatasetEnvironmentRegistry(),
                    viewManager,
                    seriesInstanceUidOf(image.seriesUid),
                    image.grid.indexToVoxelCenterWorldSpace(voxelIndex)
                )
            );
        },
        [image, viewManager]
    );
    const isImageShown = views.length > 0;
    const transversalView = useMemo(
        () =>
            views.find(
                (view) => view.label === Orientations.TRANSVERSAL_ORIENTATION
            ),
        [views]
    );
    return useMemo((): GridWidget[] => {
        if (!isOpen || !image) {
            return [];
        }
        return [
            {
                id: 'image-overview',
                title: intl.formatMessage({ id: 'series.mipPreview' }),
                subject: seriesNameOf(
                    seriesDescription,
                    intl.formatMessage({ id: 'unknown.series' })
                ),
                hostSeriesUid: seriesInstanceUidOf(image.seriesUid),
                openingSize: OPENING_SIZE,
                onClose: close,
                body: () => (
                    <FittedSeriesMipPane
                        image={image}
                        environment={environment}
                        view={transversalView}
                        onLocate={isImageShown ? locate : undefined}
                    />
                ),
            },
        ];
    }, [
        intl,
        isOpen,
        image,
        seriesDescription,
        environment,
        transversalView,
        isImageShown,
        locate,
        close,
    ]);
}
